(function () {
  const OVERLAY_ID = 'multi-variant-impact-overlay';
  const MODAL_ID = 'multi-variant-impact-modal';

  const TITLE = 'Your cart contains multiple sizes';
  const BODY_TEXT =
    'Returns have an environmental impact. We encourage you to consult the size chart and Find Your Size feature on each product page to determine the best fit.<br/><br/>If you need further assistance, email <a href="mailto:support@emmeparsons.com">support@emmeparsons.com</a> or call at 917-730-7702.';

  function findCartDrawerRoot() {
    return (
      document.querySelector('cart-drawer') ||
      document.querySelector('[data-cart-drawer]') ||
      document.querySelector('#CartDrawer') ||
      document.querySelector('.CartDrawer')
    );
  }

  function ensureDrawerPositioning(drawer) {
    // Overlay needs a positioning context. Do this only if necessary.
    const pos = window.getComputedStyle(drawer).position;
    if (pos === 'static') drawer.style.position = 'relative';
  }

  async function getCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Failed to fetch /cart.js');
    return res.json();
  }

  function isOverlayOpen() {
    const drawer = findCartDrawerRoot();
    if (!drawer) return false;
    return !!drawer.querySelector(`#${OVERLAY_ID}`);
  }

  function closeOverlay() {
    const drawer = findCartDrawerRoot();
    if (!drawer) return;

    const overlay = drawer.querySelector(`#${OVERLAY_ID}`);
    const modal = drawer.querySelector(`#${MODAL_ID}`);

    if (modal) modal.classList.remove('is-active');

    window.setTimeout(() => {
      if (overlay) overlay.remove();
    }, 320);
  }

  function openOverlay() {
    const drawer = findCartDrawerRoot();
    if (!drawer) return;
    if (isOverlayOpen()) return;

    ensureDrawerPositioning(drawer);

    // Overlay wrapper inside the cart drawer (critical fix)
    const overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.className = 'multi-variant-impact-overlay';

    // Backdrop inside drawer only
    const backdrop = document.createElement('div');
    backdrop.className = 'multi-variant-impact-backdrop';

    // Clicking backdrop closes ONLY the modal (and does not count as "outside cart")
    backdrop.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      closeOverlay();
    });

    // Modal
    const modal = document.createElement('div');
    modal.id = MODAL_ID;
    modal.className = 'multi-variant-impact-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', TITLE);

    // Prevent clicks inside modal from bubbling to any drawer handlers
    modal.addEventListener('click', function (e) {
      e.stopPropagation();
    });

    modal.innerHTML = `
      <div class="multi-variant-impact-modal__header">
        <h2 class="h6">${TITLE}</h2>

        <dialog-close-button style="display: contents">
          <button type="button" part="close-button tap-area" aria-label="Close">
            <svg aria-hidden="true" focusable="false" fill="none" width="14" class="icon icon-close" viewBox="0 0 16 16">
              <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1.5"/>
            </svg>
          </button>
        </dialog-close-button>
      </div>

      <div class="multi-variant-impact-modal__body">
        <p>${BODY_TEXT}</p>
      </div>
    `;

    // Close button closes ONLY this overlay
    const closeBtn = modal.querySelector('button[aria-label="Close"]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        closeOverlay();
      });
    }

    overlay.appendChild(backdrop);
    overlay.appendChild(modal);

    // Append overlay into the drawer so it cannot trigger "outside drawer" close
    drawer.appendChild(overlay);

    // Trigger slide-in
    requestAnimationFrame(() => {
      modal.classList.add('is-active');
    });

    // Escape-to-close (scoped; does not touch cart)
    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeOverlay();
        document.removeEventListener('keydown', onKeyDown, true);
      }
    }
    document.addEventListener('keydown', onKeyDown, true);
  }

  function findItemByVariantId(cart, variantId) {
    return cart?.items?.find((it) => String(it.variant_id) === String(variantId)) || null;
  }

  // A "style" is one product_id (color is its own product here); a "size" is a variant_id.
  // Number of distinct sizes of the given style currently in the cart.
  function distinctSizeCount(cart, productId) {
    const sizes = new Set();
    (cart?.items || []).forEach((it) => {
      if (String(it.product_id) === String(productId)) sizes.add(String(it.variant_id));
    });
    return sizes.size;
  }

  // Whether any style OTHER than the given one already has 2+ distinct sizes.
  function anotherStyleHasMultipleSizes(cart, productId) {
    const counts = {};
    (cart?.items || []).forEach((it) => {
      const pid = String(it.product_id);
      if (pid === String(productId)) return;
      (counts[pid] = counts[pid] || new Set()).add(String(it.variant_id));
    });
    return Object.keys(counts).some((pid) => counts[pid].size >= 2);
  }

  // Warn only when THIS add escalates size-bracketing:
  //   - the added style now has 3+ sizes, OR
  //   - the added style now has 2+ sizes AND another style also has 2+ sizes.
  // Because the check is scoped to the just-added style, adding a lone new style
  // (1 size) can never (re)warn, even if the cart already qualifies elsewhere.
  function shouldWarnAfterAdd(cart, productId) {
    const n = distinctSizeCount(cart, productId);
    if (n >= 3) return true;
    if (n >= 2 && anotherStyleHasMultipleSizes(cart, productId)) return true;
    return false;
  }

  // Token ensures only latest add attempt can trigger
  let activeAddToken = 0;
  let modalDelayTimer = null;

  document.addEventListener(
    'submit',
    async function (e) {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      // PDP add-to-cart form
      if (!form.action || form.action.indexOf('/cart/add') === -1) return;

      const variantId = new FormData(form).get('id');
      if (!variantId) return;

      const myToken = ++activeAddToken;

      // Snapshot cart BEFORE add
      let cartBefore = null;
      try {
        cartBefore = await getCart();
      } catch (err) {
        cartBefore = null;
      }

      // If exact variant already in cart, treat as qty increase; do not show modal
      if (findItemByVariantId(cartBefore, variantId)) return;

      // After add: check a few times to account for cart update timing
      [500, 1200, 2000].forEach((ms) => {
        window.setTimeout(async () => {
          if (activeAddToken !== myToken) return;

          let cartAfter;
          try {
            cartAfter = await getCart();
          } catch (err) {
            return;
          }

          const addedItemAfter = findItemByVariantId(cartAfter, variantId);
          if (!addedItemAfter) return;

          const productId = addedItemAfter.product_id;

          // Only trigger if this add escalates size-bracketing (see shouldWarnAfterAdd)
          if (!shouldWarnAfterAdd(cartAfter, productId)) return;

          // Do not stack timers / overlays
          if (modalDelayTimer || isOverlayOpen()) return;

          modalDelayTimer = window.setTimeout(async () => {
            modalDelayTimer = null;
            if (activeAddToken !== myToken) return;

            // Sanity: still qualifies for a warning now
            try {
              const fresh = await getCart();
              const freshItem = findItemByVariantId(fresh, variantId);
              if (!freshItem) return;
              if (!shouldWarnAfterAdd(fresh, freshItem.product_id)) return;
            } catch (err) {
              // non-fatal; proceed
            }

            openOverlay();
          }, 2000);
        }, ms);
      });
    },
    true
  );
})();
